import React, { createContext, useState, useEffect, useCallback } from "react";
import * as api from '../api';
import { useSocket } from "../hooks/useSocket";
import { toast } from 'react-toastify';


export const ShopContext = createContext(null);

const ShopContextProvider = (props) => {
    const [all_products, setAllProducts] = useState([]);
    const [cartItems, setCartItems] = useState({});
    const [user, setUser] = useState(null);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [ordersLoading, setOrdersLoading] = useState(true);

    // --- NEW: Delivery Settings & Address ---
    const [deliverySettings, setDeliverySettings] = useState({
        mode: 'locations',
        locations: []
    });

    // --- DYNAMIC SHOP STATUS ---
    const [shopStatus, setShopStatus] = useState({
        isShopCurrentlyOpen: false,
        shopOpenTime: "18:00",
        shopCloseTime: "21:00"
    });

    const VENDOR_ID = process.env.REACT_APP_VENDOR_ID;
    const { isOrderUpdated, resetOrderUpdateFlag } = useSocket(user?._id);

    // --- NEW: useEffect to fetch and periodically check shop status from the backend ---
    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const { data } = await api.getShopStatus(VENDOR_ID);
                setShopStatus(data);
                if (data.deliverySettings) {
                    setDeliverySettings(data.deliverySettings);
                }
            } catch (error) {
                console.error("Failed to fetch shop status:", error);
                // Fallback to a closed state on error
                setShopStatus({
                    isShopCurrentlyOpen: false,
                    shopOpenTime: "18:00",
                    shopCloseTime: "21:00"
                });
            }
        };

        fetchStatus(); // Fetch immediately on mount
        const intervalId = setInterval(fetchStatus, 60000); // Re-fetch every minute
        return () => clearInterval(intervalId); // Cleanup on unmount
    }, [VENDOR_ID]); // Run only once

    const fetchOrders = useCallback(async (userId) => {
        if (!userId) return;
        setOrdersLoading(true);
        try {
            const { data } = await api.getUserOrders(userId);
            setOrders(data);
        } catch (error) {
            console.error("Failed to fetch orders:", error.response?.data?.message || error.message);
        } finally {
            setOrdersLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isOrderUpdated && user) {
            fetchOrders(user._id);
            resetOrderUpdateFlag();
        }
    }, [isOrderUpdated, user, resetOrderUpdateFlag, fetchOrders]);

    const fetchProducts = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.getAvailableVendorItems(VENDOR_ID);
            setAllProducts(data);

            // --- NEW LOGIC to load cart from localStorage ---
            const savedCart = localStorage.getItem('cart');
            const parsedSavedCart = savedCart ? JSON.parse(savedCart) : {};

            // Create a default cart structure with all available product IDs set to 0
            const defaultCart = data.reduce((acc, product) => ({ ...acc, [product._id]: 0 }), {});

            // Create the final cart:
            // 1. Start with the default cart (all products at 0)
            // 2. Iterate through the saved cart
            // 3. If a saved item is still in the default cart (meaning the product is still available),
            //    use the saved quantity.
            const finalCart = { ...defaultCart };
            for (const productId in parsedSavedCart) {
                if (finalCart.hasOwnProperty(productId)) {
                    finalCart[productId] = parsedSavedCart[productId];
                }
            }
            setCartItems(finalCart); // Set the merged cart
        } catch (error) {
            console.error("Failed to fetch products:", error.response?.data?.message || error.message);
        } finally {
            setLoading(false);
        }
    }, [VENDOR_ID]); // VENDOR_ID removed from dependency array, assuming it's constant

    const fetchUserProfile = useCallback(async () => {
        try {
            const { data } = await api.getUserProfile();
            const stored = localStorage.getItem('user');
            const fullUser = stored ? { ...JSON.parse(stored), ...data.user } : data.user;
            setUser(fullUser);
            localStorage.setItem('user', JSON.stringify(fullUser));
        } catch (error) {
            console.error("Failed to fetch user profile:", error);
        }
    }, []);

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            const userData = JSON.parse(storedUser);
            setUser(userData);
            fetchOrders(userData._id);
            fetchUserProfile();
        } else {
            setOrdersLoading(false);
        }
        fetchProducts();
    }, [fetchProducts, fetchOrders, fetchUserProfile]);


    useEffect(() => {
        // We check if all_products has loaded, otherwise cartItems might be {}
        // and we would incorrectly clear localStorage.
        if (all_products.length > 0) {
            const totalItems = Object.values(cartItems).reduce((a, b) => a + b, 0);
            if (totalItems > 0) {
                // If there are items in the cart, save them
                localStorage.setItem('cart', JSON.stringify(cartItems));
            } else {
                // If the cart is empty (by user action or logout), clear it
                localStorage.removeItem('cart');
            }
        }
    }, [cartItems, all_products]);

    const login = async (userData) => {
        try {
            const { data } = await api.login(userData);
            const loggedInUser = { ...data.user, token: data.token };
            localStorage.setItem('user', JSON.stringify(loggedInUser));
            setUser(loggedInUser);
            await fetchOrders(loggedInUser._id);
            return { success: true };
        } catch (error) {
            const errorMessage = error.response?.data?.message || "Login failed. Please try again.";
            return { success: false, error: errorMessage };
        }
    };

    const register = async (userData) => {
        try {
            const { data } = await api.register({
                username: userData.username,
                password: userData.password,
                role: 'student',
                vendorId: VENDOR_ID // --- FIX: Send Vendor ID to link student to vendor ---
            });
            const loggedInUser = { ...data.user, token: data.token };
            localStorage.setItem('user', JSON.stringify(loggedInUser));
            setUser(loggedInUser);
            await fetchOrders(loggedInUser._id);
            return { success: true };
        } catch (error) {
            const errorMessage = error.response?.data?.message || "Registration failed. Please try again.";
            return { success: false, error: errorMessage };
        }
    };

    const logout = () => {
        localStorage.removeItem('user');
        setUser(null);
        setOrders([]);
        setCartItems({});
    };

    const updateProfile = async (userData) => {
        try {
            const { data } = await api.updateUserProfile(userData);
            const updatedUser = { ...user, ...data.user };
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
            return { success: true, message: data.message };
        } catch (error) {
            const errorMessage = error.response?.data?.message || "Failed to update profile.";
            return { success: false, error: errorMessage };
        }
    };

    const updatePassword = async (passwordData) => {
        try {
            const { data } = await api.updateUserPassword(passwordData);
            return { success: true, message: data.message };
        } catch (error) {
            const errorMessage = error.response?.data?.message || "Failed to update password.";
            return { success: false, error: errorMessage };
        }
    };

    const addToCart = (itemId) => {
        if (!shopStatus.isShopCurrentlyOpen) {
            // Convert 24h to 12h for the toast message
            const formatTime = (time24) => {
                if (!time24 || typeof time24 !== 'string') return '';
                const [hour, minute] = time24.split(':');
                const h = parseInt(hour, 10);
                const ampm = h >= 12 ? 'PM' : 'AM';
                const formattedHour = h % 12 || 12;
                return `${formattedHour}:${minute} ${ampm}`;
            };
            toast.warn(`Sorry, the shop is currently closed. Open from ${formatTime(shopStatus.shopOpenTime)} to ${formatTime(shopStatus.shopCloseTime)}.`);
            return;
        }
        setCartItems((prev) => ({ ...prev, [itemId]: (prev[itemId] || 0) + 1 }));
    };

    const removeFromCart = (itemId, mode = 'single') => {
        setCartItems((prev) => {
            const newCart = { ...prev };
            if (!newCart[itemId]) return newCart;
            if (mode === 'all' || newCart[itemId] <= 1) {
                newCart[itemId] = 0;
            } else {
                newCart[itemId] -= 1;
            }
            return newCart;
        });
    };

    const updateCustomerAddress = async (address) => {
        try {
            const { data } = await api.updateUserAddress({ address });
            const updatedUser = { ...user, defaultAddress: data.defaultAddress };
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser)); // Persist to storage
            return { success: true, message: data.message };
        } catch (error) {
            console.error("Update address failed:", error.response?.data?.message || error.message);
            return { success: false, error: error.response?.data?.message || "Failed to update address" };
        }
    };

    const getTotalAmount = () => {
        return Object.entries(cartItems).reduce((total, [itemId, quantity]) => {
            if (quantity > 0) {
                const itemInfo = all_products.find((product) => product._id === itemId);
                return itemInfo ? total + itemInfo.price * quantity : total;
            }
            return total;
        }, 0);
    };

    const getTotalCartItems = () => {
        return Object.values(cartItems).reduce((total, quantity) => total + (quantity > 0 ? quantity : 0), 0);
    };

    const getDeliveryCharge = (location) => {
        if (!location || location === 'Shop Pickup' || getTotalCartItems() === 0) {
            return 0;
        }

        if (deliverySettings.mode === 'locations') {
            const locObj = deliverySettings.locations.find(l => l.name === location);
            return locObj ? locObj.price : 0;
        }

        return 0;
    };

    const getTotalCartAmountWithDelivery = (location) => {
        const subtotal = getTotalAmount();
        const deliveryCharge = getDeliveryCharge(location);
        return subtotal + deliveryCharge;
    };

    const handleCheckout = async (deliveryLocation) => {
        if (!shopStatus.isShopCurrentlyOpen) {
            const formatTime = (time24) => {
                if (!time24 || typeof time24 !== 'string') return '';
                const [hour, minute] = time24.split(':');
                const h = parseInt(hour, 10);
                const ampm = h >= 12 ? 'PM' : 'AM';
                const formattedHour = h % 12 || 12;
                return `${formattedHour}:${minute} ${ampm}`;
            };
            return { success: false, error: `The shop is currently closed. Please try again between ${formatTime(shopStatus.shopOpenTime)} - ${formatTime(shopStatus.shopCloseTime)}.` };
        }
        if (!user) {
            return { success: false, error: "Please log in to place an order." };
        }
        if (!user.phoneNumber) {
            return {
                success: false,
                error: "Please add your phone number on your profile page.",
                code: "PHONE_REQUIRED"
            };
        }

        const finalTotalAmount = getTotalCartAmountWithDelivery(deliveryLocation);

        if (finalTotalAmount === 0 && getDeliveryCharge(deliveryLocation) === 0) {
            return { success: false, error: "Your cart is empty." };
        }
        const isAddressMode = deliverySettings?.mode === 'address';
        const finalDeliveryLocation = (isAddressMode && deliveryLocation !== 'Shop Pickup')
            ? 'Manual Address Delivery'
            : deliveryLocation;

        if (!deliveryLocation && !isAddressMode) {
            return { success: false, error: "Please select a delivery location." };
        }

        if (isAddressMode && !deliveryLocation && !user?.defaultAddress) {
            return { success: false, error: "Please add a delivery address." };
        }

        if (isAddressMode && !deliveryLocation && !user?.defaultAddress) {
            return { success: false, error: "Please add a delivery address." };
        }

        // --- TEST MODE BYPASS ---
        if (shopStatus.paymentMode === 'test') {
            try {
                const orderItems = all_products
                    .filter(item => cartItems[item._id] > 0)
                    .map(item => ({
                        itemId: item._id, name: item.name, price: item.price, quantity: cartItems[item._id],
                    }));

                const orderPayload = {
                    studentId: user._id,
                    vendorId: VENDOR_ID,
                    items: orderItems,
                    totalAmount: finalTotalAmount,
                    deliveryLocation: finalDeliveryLocation,
                    deliveryAddress: (isAddressMode && deliveryLocation !== 'Shop Pickup') ? user.defaultAddress : null,
                    contactPhone: user.phoneNumber,
                    razorpayOrderId: `TEST_ORDER_${Date.now()}`,
                    razorpayPaymentId: `TEST_PAY_${Date.now()}`,
                    paymentMode: 'test'
                };

                await api.placeOrder(orderPayload);
                setCartItems(Object.keys(cartItems).reduce((acc, key) => ({ ...acc, [key]: 0 }), {}));
                await fetchOrders(user._id);
                return { success: true, isTest: true };
            } catch (error) {
                console.error("Test order placement failed:", error);
                return { success: false, error: "Failed to place test order." };
            }
        }
        // ------------------------

        try {
            // Pass VENDOR_ID so backend knows which keys to use
            const { data: razorpayOrder } = await api.createRazorpayOrder({
                amount: finalTotalAmount,
                vendorId: VENDOR_ID
            });

            return new Promise((resolve) => {
                const options = {
                    key: razorpayOrder.key, // Use the key returned from backend
                    amount: razorpayOrder.amount,
                    currency: "INR",
                    name: "Madurai Special",
                    order_id: razorpayOrder.id,
                    handler: async function (response) {
                        try {
                            const orderItems = all_products
                                .filter(item => cartItems[item._id] > 0)
                                .map(item => ({
                                    itemId: item._id, name: item.name, price: item.price, quantity: cartItems[item._id],
                                }));

                            const orderPayload = {
                                studentId: user._id,
                                vendorId: VENDOR_ID,
                                items: orderItems,
                                totalAmount: finalTotalAmount,
                                deliveryLocation: finalDeliveryLocation,
                                deliveryAddress: (isAddressMode && deliveryLocation !== 'Shop Pickup') ? user.defaultAddress : null,
                                contactPhone: user.phoneNumber,
                                razorpayOrderId: response.razorpay_order_id,
                                razorpayPaymentId: response.razorpay_payment_id,
                            };

                            await api.placeOrder(orderPayload);
                            setCartItems(Object.keys(cartItems).reduce((acc, key) => ({ ...acc, [key]: 0 }), {}));
                            await fetchOrders(user._id);
                            resolve({ success: true });
                        } catch (error) {
                            console.error("Order placement failed:", error);
                            resolve({ success: false, error: "Failed to place order after payment." });
                        }
                    },
                    prefill: {
                        name: user.username,
                        contact: user.phoneNumber
                    },
                    modal: {
                        ondismiss: function () {
                            console.log("Checkout form closed");
                            resolve({ success: false, error: "Payment was cancelled." });
                        }
                    }
                };
                const rzp = new window.Razorpay(options);
                rzp.open();
            });
        } catch (error) {
            console.error("Checkout failed:", error.response?.data?.message || error.message);
            return { success: false, error: "Payment process failed. Please try again." };
        }
    };

    const contextValue = {
        user,
        login,
        register,
        logout,
        all_products,
        cartItems,
        addToCart,
        removeFromCart,
        getTotalAmount,
        getTotalCartItems,
        handleCheckout,
        orders,
        fetchOrders,
        updateProfile,
        updatePassword,
        loading,
        ordersLoading,
        getDeliveryCharge,
        getTotalCartAmountWithDelivery,
        shopStatus, // Expose the full shopStatus object
        deliverySettings,
        updateCustomerAddress
    };

    return (
        <ShopContext.Provider value={contextValue}>
            {props.children}
        </ShopContext.Provider>
    );
};

export default ShopContextProvider;